import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Avatar from 'flarum/common/components/Avatar';
import extractText from 'flarum/common/utils/extractText';
const DEBOUNCE_MS = 300;
// SocietyMembers::MIN_QUERY_LENGTH on the PHP side.
const MIN_QUERY_LENGTH = 2;
function t(key, params) {
  if (params === void 0) {
    params = {};
  }
  return app.translator.trans(`champis-exhibitions.forum.society_page.members_${key}`, params);
}

/**
 * Who belongs to the société, on its page: any member can add a champis.net
 * account, found by pseudo or name, and remove another or themselves - see
 * SocietyMembers on the PHP side. Never its last member, whose button isn't
 * offered (the server refuses it anyway).
 */
export default class SocietyMembersSection extends Component {
  members = null;
  query = '';
  results = null;
  open = false;
  busy = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.request('GET', '/members').then(response => {
      this.members = response.data;
      m.redraw();
    });
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearTimeout(this.debounce);
  }
  request(method, path, options) {
    if (options === void 0) {
      options = {};
    }
    return app.request({
      method,
      url: `${app.forum.attribute('apiUrl')}/champis-exhibitions-societies/${this.attrs.society.id()}${path}`,
      ...options
    });
  }
  view() {
    return m("div", {
      className: "ChampisSocietyPage-section"
    }, m("div", {
      className: "ChampisSocietyPage-sectionHeader"
    }, m("h3", null, t('title'))), m("div", {
      className: "ChampisSocietyPage-sectionBody"
    }, m("div", {
      className: "ChampisSocietyMembers-add"
    }, m("input", {
      className: "FormControl",
      type: "text",
      autocomplete: "off",
      placeholder: extractText(t('add_placeholder')),
      "aria-label": extractText(t('add_placeholder')),
      value: this.query,
      disabled: this.busy,
      oninput: e => this.onInput(e.target.value),
      onfocus: () => {
        this.open = this.results !== null;
      },
      onblur: () => {
        // Closes the list when the field loses focus to anything but
        // the list itself - same pattern as DiscussionFilters' authors.
        setTimeout(() => {
          this.open = false;
          m.redraw();
        }, 150);
      }
    }), this.open && this.results && m("ul", {
      className: "ChampisSocietyMembers-results",
      onmousedown: e => e.preventDefault()
    }, this.results.length === 0 ? m("li", {
      key: "none",
      className: "ChampisSocietyMembers-noResult"
    }, t('no_result')) : this.results.map(member => m("li", {
      key: member.id,
      onclick: () => this.add(member)
    }, this.person(member)))), m("p", {
      className: "ChampisSocietyMembers-hint"
    }, t('hint'))), this.members === null ? m(LoadingIndicator, null) : m("ul", {
      className: "ChampisSocietyPage-toxicities"
    }, this.members.map(member => this.row(member, this.members.length > 1)))));
  }
  row(member, removable) {
    const isSelf = String(member.id) === app.session.user?.id();
    return m("li", {
      key: member.id,
      className: "ChampisSocietyPage-toxicity"
    }, m("span", {
      className: "ChampisSocietyPage-toxicityName"
    }, this.person(member, isSelf)), removable && m("div", {
      className: "ChampisSocietyPage-rowActions"
    }, m("a", {
      "aria-label": extractText(t('remove_button')),
      title: extractText(t('remove_button')),
      onclick: () => this.remove(member, isSelf)
    }, m("i", {
      className: "icon fas fa-user-minus",
      "aria-hidden": "true"
    }))));
  }

  /** Its pseudo beside its name, to tell two of the same name apart. */
  person(member, isSelf) {
    if (isSelf === void 0) {
      isSelf = false;
    }
    const user = app.store.createRecord('users', {
      attributes: {
        username: member.username,
        displayName: member.displayName,
        avatarUrl: member.avatarUrl
      }
    });
    return m("span", {
      className: "ChampisSocietyMembers-person"
    }, m(Avatar, {
      user: user,
      className: "ChampisSocietyMembers-avatar"
    }), m("span", null, member.displayName), member.username !== member.displayName && m("span", {
      className: "ChampisSocietyMembers-username"
    }, member.username), isSelf && m("span", {
      className: "ChampisSocietyMembers-you"
    }, "(", t('you'), ")"));
  }
  onInput(value) {
    this.query = value;
    clearTimeout(this.debounce);
    const query = value.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      this.results = null;
      this.open = false;
      return;
    }
    this.debounce = setTimeout(() => {
      this.request('GET', '/member-candidates', {
        params: {
          q: query
        }
      }).then(response => {
        // Stale response for a query the user has since changed.
        if (query !== this.query.trim()) return;
        this.results = response.data;
        this.open = true;
        m.redraw();
      });
    }, DEBOUNCE_MS);
  }
  add(member) {
    this.busy = true;
    this.open = false;
    this.request('POST', '/members', {
      body: {
        userId: member.id
      }
    }).then(response => {
      this.members = response.data;
      this.query = '';
      this.results = null;
      app.alerts.show({
        type: 'success'
      }, t('added', {
        name: member.displayName
      }));
    }).catch(() => {
      // app.request's own alert says why.
    }).finally(() => {
      this.busy = false;
      m.redraw();
    });
  }
  remove(member, isSelf) {
    const message = isSelf ? t('leave_confirm') : t('remove_confirm', {
      name: member.displayName
    });
    if (!confirm(extractText(message))) return;
    this.request('DELETE', `/members/${member.id}`).then(response => {
      // A full reload: the rest of the session still has the access it lost.
      if (isSelf) {
        window.location.href = app.route('exhibitions');
        return;
      }
      this.members = response.data;
      app.alerts.show({
        type: 'success'
      }, t('removed', {
        name: member.displayName
      }));
      m.redraw();
    }).catch(() => {
      // app.request's own alert says why.
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/SocietyMembersSection', SocietyMembersSection);